import { Button, Col, Grid, message, Modal, Row, Select } from 'antd';
import Scrollbars from 'rc-scrollbars';
import React, { useEffect, useRef, useState } from 'react';
import PropertyDetail from '../routes/PropertyDetail';
import {
  useGetPersonalPropertyByIdQuery,
  useGetPropertyWatchListByIdQuery,
  usePostPersonalPropertyMutation,
  useUpdatePersonalPropertyMutation,
} from '@/pages/mortgage/apis/propertyAPI';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import propertyDetailSchema from '../schema/PropertyDetailSchema';
import ApplicationHeading from '@/components/Elements/Heading/ApplicationHeading';
import {
  InputField,
  ListOptionsField,
  RadioGroupField,
  SelectField,
} from '@/components/Form';
import {
  AGREE_OPTIONS,
  FREQUENCY_OPTIONS,
  LENDER_OPTIONS,
  OVERSEAS_PROPERTY_PURPOSE_OPTIONS,
  PROPERTY_LOCATION_TYPE_OPTIONS,
  PROPERTY_OWNER_TYPE_OPTIONS,
  PROPERTY_USE_TYPE_OPTIONS,
  RATE_TYPE_OPTIONS,
  PROPERTY_LENDER_OPTIONS,
} from '@/constant/options';
import LocationField from '@/components/LocationField';
import GoogleCustomLocationField from '@/components/Elements/GoogleCustomLocation';
import makeAllFieldsOptional from '@/utils/yupUtils';
import { ObjectSchema } from 'yup';
import {
  PROPERTY_PURPOSE_OPTIONS_REFINANCE,
  PROPERTY_REFINANCE_OPTIONS,
  TYPE_OF_INTEREST_RATE_OPTIONS,
} from '@/pages/mortgage/routes/Refinance/constant/optionList';
import { useCountryOptions } from '@/hooks/useCountryOptions';
interface PropertyFormModalProps {
  overseas?: boolean;
  isModalOpen: boolean;
  closeModal: () => void;
  propertyOption: 'personal' | 'watchlist';
  type?: 'add' | 'edit';
  id?: any;
  refetchPersonalProperties?: () => void;
  propertyType?: 'personal' | 'watchlist';
  refetchWatchlistProperties?: () => void;
  onNewPropertyAdded?: any;
  refetchPropertyData?: () => void;
  formSource?: string;
}

const { useBreakpoint } = Grid;

const PropertyFormModal: React.FC<PropertyFormModalProps> = ({
  overseas = false,
  isModalOpen,
  closeModal,
  refetchPersonalProperties,
  refetchWatchlistProperties,
  onNewPropertyAdded,
  type = 'add',
  id,
  refetchPropertyData: refetchAllPropertyData,
  formSource = 'purchase',
}) => {
  const screen = useBreakpoint();
  const size = screen?.md ? 'large' : 'middle';
  const [propertyDetailsObject, setPropertyDetailsObject] = useState<any>(null);

  const methods = useForm({
    defaultValues: {
      propertyLocation: overseas ? 'overseas' : 'australian',
    },
    resolver: yupResolver(
      makeAllFieldsOptional(propertyDetailSchema) as ObjectSchema<any>,
    ),
  });
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    resetField,
  } = methods;

  const isMortgage = watch('isMortgageOnProperty');

  const watchPropertyLocation = watch('propertyLocation');
  const watchPropertyPurpose = watch('propertyPurpose');
  const watchPropertyUse = watch('propertyUse');

  const { data: propertyData, refetch: refetchPropertyData } =
    useGetPersonalPropertyByIdQuery(
      { id: id },
      {
        skip: !id || type === 'add' || !isModalOpen,
        refetchOnMountOrArgChange: true,
      },
    );
  const [postProperty, { isLoading: personalPropertyLoading }] =
    usePostPersonalPropertyMutation();

  const [updateProperty, { isLoading: personalPropertyUpdateLoading }] =
    useUpdatePersonalPropertyMutation();

  useEffect(() => {
    if (isModalOpen && type === 'add') {
      reset({
        propertyLocation: overseas ? 'overseas' : 'australian',
      });
    }
    if (isModalOpen && type === 'edit' && id) {
      refetchPropertyData();
    }
  }, [isModalOpen, type, overseas, reset]);

  const onSubmit = async (data: any) => {
    const { propertyLocation, propertyUse, isMortgageOnProperty } = data;

    console.log('📦 propertyAddressObject:', propertyDetailsObject);

    const propertyDetails: any = {
      propertyAddress: data.propertyAddress,
      propertyValue: data.propertyValue,
      isMortgageOnProperty,
      propertyOwner: data.propertyOwner,
      propertyUse,
      propertyRentFrequency: data.propertyRentFrequency,
      propertyAddressObject: propertyDetailsObject,
      country: data.propertyCountry,
    };

    if (
      propertyUse === 'rented' ||
      propertyUse === 'short_term_lease' ||
      propertyUse === 'commercial' ||
      propertyUse === 'investment_property'
    ) {
      propertyDetails.propertyRent = data.propertyRent;
    }

    if (isMortgageOnProperty) {
      propertyDetails.mortgageLoanLimit = data.mortgageLoanLimit;
      propertyDetails.mortgageLoanTerm = data.mortgageLoanTerm;
      propertyDetails.mortgageInterest = data.mortgageInterest;
      propertyDetails.mortgageInterestType = data.mortgageInterestType;

      if (propertyLocation === 'overseas') {
        propertyDetails.repaymentSum = data.repaymentSum;
      } else {
        propertyDetails.mortgageLender = data.mortgageLender;
      }
    }

    if (formSource === 'refinance') {
      propertyDetails.propertyPurpose = data.propertyPurpose;
      propertyDetails.propertyAmountRequired = data.propertyAmountRequired;
      propertyDetails.propertyAmountPurpose = data.propertyAmountPurpose;
      // propertyDetails.refinanceInterestType = data.refinanceInterestType;
      propertyDetails.loanOnFixedRate = data.loanOnFixedRate;
    }

    const payload = {
      propertyLocation,
      propertyDetails,
    };

    try {
      if (type === 'edit' && id) {
        await updateProperty({ property_id: id, data: payload }).unwrap();
        message.success('Property updated successfully');
      } else {
        const res = await postProperty(payload).unwrap();
        if (res?.data?.id) {
          message.success('Successfully submitted details');
          onNewPropertyAdded?.(res?.data);
        }
      }

      closeModal();
      reset();
      refetchPersonalProperties?.();
      refetchWatchlistProperties?.();
      refetchAllPropertyData?.();
    } catch (error: any) {
      message.error('Something went wrong.');
    }
  };

  useEffect(() => {
    setValue('propertyLocation', overseas ? 'overseas' : 'australian');
  }, [overseas, setValue]);

  useEffect(() => {
    if (type === 'edit' && propertyData && isModalOpen) {
      const details = propertyData?.data?.propertyDetails;

      setValue('propertyAddress', details?.property_address ?? null);
      setValue('propertyValue', details?.property_value ?? null);
      setValue(
        'isMortgageOnProperty',
        details?.is_mortgage_on_property ?? null,
      );
      if (details?.is_mortgage_on_property) {
        setValue('mortgageLoanLimit', details?.mortgage_loan_limit ?? null);
        setValue('mortgageLender', details?.mortgage_lender ?? null);
        setValue('mortgageLoanTerm', details?.mortgage_loan_term ?? null);
        setValue('mortgageInterest', details?.mortgage_interest ?? null);
        setValue(
          'mortgageInterestType',
          details?.mortgage_interest_type ?? null,
        );
        if (propertyData?.data?.propertyLocation === 'overseas') {
          setValue('repaymentSum', details?.repayment_sum);
        }
      }
      setValue('propertyOwner', details?.property_owner ?? null);
      setValue('propertyUse', details?.property_use ?? null);
      if (
        details?.property_use === 'rented' ||
        details?.property_use == 'short_term_lease' ||
        details?.property_use == 'commercial' ||
        details?.property_use == 'investment_property'
      ) {
        setValue('propertyRent', details?.property_rent ?? null);
      }
      setValue(
        'propertyRentFrequency',
        details?.property_rent_frequency ?? null,
      );
      setValue(
        'propertyLocation',
        propertyData?.data?.propertyLocation ?? null,
      );
      if (formSource === 'refinance') {
        setValue('propertyPurpose', details?.property_purpose ?? null);
        setValue(
          'propertyAmountRequired',
          details?.property_amount_required ?? null,
        );
        setValue(
          'propertyAmountPurpose',
          details?.property_amount_purpose ?? null,
        );
        setValue(
          'refinanceInterestType',
          details?.refinance_interest_type ?? null,
        );
        setValue('loanOnFixedRate', details?.loan_on_fixed_rate ?? null);
      }
      setValue('propertyCountry', details?.country ?? null);
    }
  }, [propertyData, setValue, formSource, isModalOpen, type]);

  useEffect(() => {
    if (isMortgage === false) {
      resetField('mortgageLoanLimit');
      resetField('mortgageLender');
      resetField('mortgageLoanTerm');
      resetField('mortgageInterest');
      resetField('mortgageInterestType');
    }
  }, [resetField, isMortgage]);

  const watchRefinance = watch('propertyPurpose');

  useEffect(() => {
    if (formSource !== 'refinance' || watchPropertyLocation !== 'australian') {
      // If refinance block is not active, clear all refinance-related fields
      resetField('propertyPurpose');
      resetField('propertyAmountRequired');
      resetField('propertyAmountPurpose');
      resetField('refinanceInterestType');
    } else {
      // Only show/hide based on purpose
      if (
        watchPropertyPurpose !== 'refinance_and_cash_out' &&
        watchPropertyPurpose !== 'cash_out_only'
      ) {
        resetField('propertyAmountRequired');
        resetField('propertyAmountPurpose');
      }

      if (
        watchPropertyPurpose !== 'refinance' &&
        watchPropertyPurpose !== 'refinance_and_cash_out'
      ) {
        resetField('refinanceInterestType');
      }
    }
  }, [formSource, watchPropertyLocation, watchPropertyPurpose, resetField]);

  const countryOptions = useCountryOptions();

  return (
    <Modal
      open={isModalOpen}
      onCancel={() => {
        closeModal();
      }}
      maskClosable={false}
      width={1000}
      footer={[
        <>
          <Button
            key="submit"
            type="primary"
            loading={personalPropertyLoading || personalPropertyUpdateLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {type === 'edit' ? 'Update Property' : 'Add Property'}
          </Button>
          <Button key="cancel" onClick={closeModal}>
            Cancel
          </Button>
        </>,
      ]}
      centered
    >
      <Scrollbars autoHeight autoHeightMax="80vh" style={{ width: '100%' }}>
        <div style={{ padding: '16px' }}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <ApplicationHeading
                style={{
                  textAlign: 'center',
                }}
                title={`${type === 'edit' ? 'Edit' : 'Add'} ${overseas ? 'Overseas' : 'Australian'} Property Detail`}
              />
              <Row gutter={[16, 16]}>
                <Col
                  xs={24}
                  style={{
                    display: 'none',
                  }}
                >
                  <RadioGroupField
                    size="large"
                    name="propertyLocation"
                    options={PROPERTY_LOCATION_TYPE_OPTIONS}
                    label="Location of the property"
                    control={control}
                  />
                </Col>
                <Col xs={24}>
                  <GoogleCustomLocationField
                    label="Property Address"
                    location={overseas ? 'overseas' : 'australia'}
                    name="propertyAddress"
                    control={control}
                    placeholder="Enter property address"
                    size={size}
                    required={true}
                    error={errors.propertyAddress?.message?.toString() ?? ''}
                    setPropertyDetailsObject={setPropertyDetailsObject}
                  />
                </Col>
                {watchPropertyLocation === 'overseas' && (
                  <Col xs={24}>
                    <SelectField
                      options={countryOptions}
                      showSearch
                      placeholder="Select property country"
                      name="propertyCountry"
                      label="Property Country"
                      control={control}
                      size={size}
                      error={errors.propertyCountry?.message?.toString() ?? ''}
                    />
                  </Col>
                )}
                <Col xs={24} md={12}>
                  <InputField
                    label="Property’s Value"
                    name="propertyValue"
                    control={control}
                    placeholder={`Property Value in ${overseas ? 'local currency' : 'AUD'}`}
                    size={size}
                    type="number"
                    error={errors.propertyValue?.message?.toString() ?? ''}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <SelectField
                    name="isMortgageOnProperty"
                    options={AGREE_OPTIONS}
                    label="Do you have a mortgage on this property?"
                    placeholder="Select yes or no"
                    control={control}
                    size={size}
                    error={
                      errors.isMortgageOnProperty?.message?.toString() ?? ''
                    }
                  />
                </Col>
                {isMortgage && (
                  <Col xs={24}>
                    <Row gutter={30}>
                      <Col xs={24} md={12}>
                        <InputField
                          label="Loan Limit"
                          name="mortgageLoanLimit"
                          control={control}
                          placeholder="Enter your loan limit"
                          size={size}
                          type="number"
                          error={
                            errors.mortgageLoanLimit?.message?.toString() ?? ''
                          }
                        />
                      </Col>
                      {watchPropertyLocation === 'overseas' ? (
                        <Col xs={24} md={12}>
                          <InputField
                            name="repaymentSum"
                            placeholder="Enter your repayment sum"
                            label="Repayment Sum"
                            control={control}
                            size={size}
                            type="number"
                            error={
                              errors.repaymentSum?.message?.toString() ?? ''
                            }
                          />
                        </Col>
                      ) : (
                        <Col xs={24} md={12}>
                          <SelectField
                            options={PROPERTY_LENDER_OPTIONS}
                            showSearch
                            name="mortgageLender"
                            control={control}
                            size={size}
                            label="Lender"
                            placeholder="Select your lender"
                            error={
                              errors.mortgageLender?.message?.toString() ?? ''
                            }
                          />
                        </Col>
                      )}
                      <Col xs={24} md={12}>
                        <InputField
                          label="Remaining Loan Term"
                          name="mortgageLoanTerm"
                          control={control}
                          placeholder="Years left on loan term"
                          size={size}
                          type="number"
                          suffix="Years"
                          error={
                            errors.mortgageLoanTerm?.message?.toString() ?? ''
                          }
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <InputField
                          label="Interest Rate %"
                          name="mortgageInterest"
                          control={control}
                          placeholder="Your loan interest rate"
                          size={size}
                          suffix="%"
                          type="number"
                          allowDecimal={true}
                          error={
                            errors.mortgageInterest?.message?.toString() ?? ''
                          }
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <SelectField
                          name="mortgageInterestType"
                          options={RATE_TYPE_OPTIONS}
                          label="Type of Interest Rate"
                          control={control}
                          placeholder="Select type of interest rate"
                          size={size}
                          error={
                            errors.mortgageInterestType?.message?.toString() ??
                            ''
                          }
                        />
                      </Col>
                    </Row>
                  </Col>
                )}
                <Col xs={24} md={12}>
                  <SelectField
                    options={PROPERTY_OWNER_TYPE_OPTIONS}
                    name="propertyOwner"
                    control={control}
                    size="large"
                    label="Type of Ownership"
                    placeholder="Select type of ownership"
                    error={errors.propertyOwner?.message?.toString() ?? ''}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <SelectField
                    options={
                      watchPropertyLocation === 'australian'
                        ? PROPERTY_USE_TYPE_OPTIONS
                        : OVERSEAS_PROPERTY_PURPOSE_OPTIONS
                    }
                    name="propertyUse"
                    control={control}
                    size="large"
                    label="Property Usage"
                    placeholder="Select type of usage"
                    error={errors.propertyUse?.message?.toString() ?? ''}
                  />
                </Col>
                {(watchPropertyUse === 'rented' ||
                  watchPropertyUse === 'short_term_lease' ||
                  watchPropertyUse === 'commercial' ||
                  watchPropertyUse === 'investment_property') && (
                  <Col xs={24}>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <InputField
                          label="Property Rent"
                          name="propertyRent"
                          control={control}
                          placeholder="Your  Property Rent"
                          size={size}
                          type="number"
                          error={errors.propertyRent?.message?.toString() ?? ''}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <SelectField
                          name="propertyRentFrequency"
                          options={FREQUENCY_OPTIONS}
                          label="Frequency of Rent"
                          control={control}
                          size={size}
                          placeholder="Select frequency of rent"
                          error={
                            errors.propertyRentFrequency?.message?.toString() ??
                            ''
                          }
                        />
                      </Col>
                    </Row>
                  </Col>
                )}
                {formSource === 'refinance' &&
                  watchPropertyLocation === 'australian' && (
                    <Col xs={24}>
                      <Col xs={24}>
                        <SelectField
                          options={PROPERTY_PURPOSE_OPTIONS_REFINANCE}
                          name={`propertyPurpose`}
                          control={control}
                          size="large"
                          label="Select the purpose"
                          placeholder="Select the purpose"
                          error={
                            (errors as any)[`propertyPurpose`]?.message ?? ''
                          }
                        />
                      </Col>
                      <Col xs={24}>
                        {(watchPropertyPurpose === 'refinance_and_cash_out' ||
                          watchPropertyPurpose === 'cash_out_only') && (
                          <Row gutter={30}>
                            <Col xs={24}>
                              <InputField
                                control={control}
                                type="number"
                                label={`What is the cash-out amount?`}
                                placeholder="Enter the refinance amount"
                                name="propertyAmountRequired"
                                error={
                                  (errors as any)[`propertyAmountRequired`]
                                    ?.message ?? ''
                                }
                                size="large"
                              />
                            </Col>
                            <Col xs={24}>
                              <SelectField
                                size={size}
                                options={PROPERTY_REFINANCE_OPTIONS}
                                control={control}
                                label={`Cash-out Purpose`}
                                placeholder="Cash-out Purpose"
                                name={`propertyAmountPurpose`}
                                error={
                                  (errors as any)[`propertyAmountPurpose`]
                                    ?.message ?? ''
                                }
                              />
                            </Col>
                          </Row>
                        )}
                        {(watchPropertyPurpose === 'refinance' ||
                          watchPropertyPurpose ===
                            'refinance_and_cash_out') && (
                          <Row gutter={30}>
                            <Col xs={24}>
                              {/* <SelectField
                                size={size}
                                control={control}
                                name="refinanceInterestType"
                                options={TYPE_OF_INTEREST_RATE_OPTIONS}
                                label="Type of interest rate in Property"
                                placeholder="Type of interest rate in property"
                                error={
                                  (errors as any)[`refinanceInterestType`]
                                    ?.message ?? ''
                                }
                              /> */}
                              <SelectField
                                size={size}
                                control={control}
                                placeholder="Is this loan currently on a fixed rate?"
                                name="loanOnFixedRate"
                                options={AGREE_OPTIONS}
                                label="Is this loan currently on a fixed rate?"
                              />
                            </Col>
                          </Row>
                        )}
                      </Col>
                    </Col>
                  )}
              </Row>
            </form>
          </FormProvider>
        </div>
      </Scrollbars>
    </Modal>
  );
};

export default PropertyFormModal;
